import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  TablePagination,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../utils/api';

const PassList = ({ eventName }) => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [passTypeFilter, setPassTypeFilter] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [passTypes, setPassTypes] = useState([]);
  const [titles, setTitles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [editingRegNo, setEditingRegNo] = useState(null);
  const [newRegNo, setNewRegNo] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);

  useEffect(() => {
    if (eventName) {
      fetchPasses();
      fetchPassTypes();
    }
  }, [categoryFilter, passTypeFilter, titleFilter, searchQuery, page, rowsPerPage, eventName]);

  const fetchPasses = async () => {
    if (!eventName) return;
    
    setLoading(true);
    try {
      let url = '/api/entry-pass-list/';
      const params = new URLSearchParams();
      if (passTypeFilter) params.append('pass_type', passTypeFilter);
      if (titleFilter) params.append('title', titleFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);
      params.append('event', eventName);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await api.get(url);
      setPasses(response.data.results);
      setTotalCount(response.data.count);
    } catch (err) {
      setError('Failed to fetch passes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPassTypes = async () => {
    if (!eventName) return;
    
    try {
      const response = await api.get(`/api/unique-access-points/?event=${eventName}`);
      setPassTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch pass types', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      setSearchQuery(event.target.value);
      setPage(0);
    }
  };

  const handleUpdateEmail = async (regNo) => {
    try {
      await api.post(`/api/update-email/`, {
        email: newEmail,
        reg_no: regNo,
        event: eventName
      });
      setEditingEmail(null);
      fetchPasses();
    } catch (error) {
      console.error('Error updating email:', error);
    }
  };

  const handleUpdateRegNo = async (pass) => {
    try {
      await api.post(`/api/confirm-spot-registration/`, {
        id: pass.id,
        reg_no: newRegNo,
        event: eventName
      });
      setEditingRegNo(null);
      fetchPasses();
    } catch (error) {
      console.error('Error updating registration number:', error);
    }
  };

  const handleUpdateName = async (regNo) => {
    try {
      await api.post(`/api/update-name/`, {
        name: newName,
        reg_no: regNo,
        event: eventName
      });
      setEditingName(null);
      fetchPasses();
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  const handleDeletePhoto = async (id) => {
    try {
      setDeletingPhotoId(id);
      await api.get(`/api/delete-photo/${id}/`);
      // Refresh the passes list to reflect the changes
      fetchPasses();
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Failed to delete photo');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  if (loading && passes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: '200px' }}
        />
        <TextField
          select
          label="Pass Type"
          variant="outlined"
          size="small"
          value={passTypeFilter}
          onChange={(e) => setPassTypeFilter(e.target.value)}
          sx={{ minWidth: '150px' }}
        >
          <MenuItem value="">All</MenuItem>
          {passTypes.map((passType) => (
            <MenuItem key={passType} value={passType}>
              {passType}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Reg No</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Institution</TableCell>
              <TableCell>Original Designation</TableCell>
              <TableCell>Event Participation</TableCell>
              <TableCell>Poster Title</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {passes.map((pass) => (
              <TableRow key={pass.uuid}>
                <TableCell>
                  {editingName === pass.reg_no ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                      />
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={() => handleUpdateName(pass.reg_no)}
                      >
                        Save
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setEditingName(null);
                          setNewName('');
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {pass.name ? pass.name : pass.designation}
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setEditingName(pass.reg_no);
                          setNewName(pass.name || '');
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {editingRegNo === pass.id ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        value={newRegNo}
                        onChange={(e) => setNewRegNo(e.target.value)}
                      />
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={() => handleUpdateRegNo(pass)}
                      >
                        Save
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setEditingRegNo(null);
                          setNewRegNo('');
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {pass.reg_no}
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setEditingRegNo(pass.id);
                          setNewRegNo(pass.reg_no || '');
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell>{pass.designation}</TableCell>
                <TableCell>
                  {editingEmail === pass.reg_no ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={() => handleUpdateEmail(pass.reg_no)}
                      >
                        Save
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setEditingEmail(null);
                          setNewEmail('');
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {pass.email}
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setEditingEmail(pass.reg_no);
                          setNewEmail(pass.email);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {pass.phone}
                </TableCell>
                <TableCell>
                  {pass.institution || '-'}
                </TableCell>
                <TableCell>
                  {pass.original_designation || '-'}
                </TableCell>
                <TableCell>
                  {pass.event_participation || '-'}
                </TableCell>
                <TableCell>
                  {pass.poster_title || '-'}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeletePhoto(pass.id)}
                    disabled={deletingPhotoId === pass.id}
                  >
                    {deletingPhotoId === pass.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default PassList;
